import React, { Fragment, useState } from 'react';
import classNames from 'classnames';

import { useIntl } from '../../../util/reactIntl';
import btnCss from '../../../components/Button/Button.module.css';

// Block components
import BlockDefault from './BlockDefault';
import BlockFooter from './BlockFooter';
import BlockSocialMediaLink from './BlockSocialMediaLink';
import SearchPopupModal from './SearchPopupModal';

import css from './BlogBuilder.module.css';
///////////////////////////////////////////
// Mapping of block types and components //
///////////////////////////////////////////

const defaultBlockComponents = {
  defaultBlock: { component: BlockDefault },
  footerBlock: { component: BlockFooter },
  socialMediaLink: { component: BlockSocialMediaLink },
};

////////////////////
// Blocks builder //
////////////////////

/**
 * @typedef {Object} BlockConfig
 * @property {string} blockId
 * @property {string} blockName
 * @property {'defaultBlock' | 'footerBlock' | 'socialMediaLink'} blockType
 */

/**
 * @typedef {Object} FieldComponentsConfig
 * @property {ReactNode} component
 * @property {Function} pickValidProps
 */

/**
 * @typedef {Object} BlockComponentConfig
 * @property {ReactNode} component
 */

/**
 * This returns an array of Block components from given block config array.
 *
 * @component
 * @param {Object} props
 * @param {Array<BlockConfig>} props.blocks - array of block configs
 * @param {Object} props.options extra options for the block component (e.g. custom fieldComponents)
 * @param {Object<string,FieldComponentsConfig>?} props.options.fieldComponents extra options for the block component (e.g. custom fieldComponents)
 * @param {Object<string,BlockComponentConfig>?} props.options.blockComponents extra options for the block component (e.g. custom fieldComponents)
 * @param {string?} props.responsiveImageSizes
 * @param {string?} props.sectionId
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string?} props.className add more styles in addition to components own css.root
 * @param {string?} props.mediaClassName add styles for the block's attached media field
 * @param {string?} props.textClassName add styles for the block's attached text field
 * @param {string?} props.ctaButtonClass add styles for the block's attached CTA field
 * @param {Object?} props.params - path params for the named route and its pathname prop
 * @returns {JSX.Element} containing form that allows adding availability exceptions
 */
const BlockBuilder = props => {
  const { blocks = [], sectionId, options, ...otherProps } = props;
  const intl = useIntl();

  const [searchModal, setSearchModal] = useState({ isOpen: false, defaultCategory: null });
  const openModal = defaultCategory => setSearchModal({ isOpen: true, defaultCategory });
  const closeModal = () => setSearchModal({ isOpen: false, defaultCategory: null });

  // Extract block & field component mappings from props
  // If external mapping has been included for fields
  // E.g. { h1: { component: MyAwesomeHeader } }
  const { blockComponents, fieldComponents } = options || {};
  const blockOptionsMaybe = fieldComponents ? { options: { fieldComponents } } : {};

  // If there's no block, we can't render the correct block component
  if (!blocks || blocks.length === 0) {
    return null;
  }

  // Selection of Block components
  // Combine component-mapping from props together with the default one:
  const components = { ...defaultBlockComponents, ...blockComponents };

  const audienceCardLinkClass = classNames(
    btnCss.primaryButtonInlineRoot,
    btnCss.primaryButton,
    css.primaryCardLink
  );

  return (
    <>
      <SearchPopupModal
        isOpen={searchModal.isOpen}
        onClose={closeModal}
        defaultCategory={searchModal.defaultCategory}
      />
      {blocks.map((block, index) => {
        const config = components[block.blockType];
        const Block = config?.component;
        const blockId = block.blockId || `${sectionId}-block-${index + 1}`;

        const isAudienceRow =
          sectionId === 'four-button' &&
          (blockId === 'for-customers' || blockId === 'for-providers');

        if (Block) {
          const sharedBlockProps = {
            ...block,
            blockId,
            ...blockOptionsMaybe,
            ...otherProps,
            ...(isAudienceRow ? { hideCallToAction: true } : {}),
          };

          if (isAudienceRow && blockId === 'for-customers') {
            return (
              <Fragment key={`${blockId}_i${index}`}>
                <div className={css.fourButtonRow}>
                  <Block {...sharedBlockProps} />
                  <div className={css.fourButtonActions}>
                    <button
                      className={audienceCardLinkClass}
                      type="button"
                      onClick={() => openModal('workspaces')}
                    >
                      {intl.formatMessage({ id: 'BlockBuilder.findASpace' })}
                    </button>
                    <a className={audienceCardLinkClass} href="/l/new">
                      {intl.formatMessage({ id: 'BlockBuilder.listYourServices' })}
                    </a>
                  </div>
                </div>
              </Fragment>
            );
          }

          if (isAudienceRow && blockId === 'for-providers') {
            return (
              <Fragment key={`${blockId}_i${index}`}>
                <div className={css.fourButtonRow}>
                  <Block {...sharedBlockProps} />
                  <div className={css.fourButtonActions}>
                    <a className={audienceCardLinkClass} href="/l/new">
                      {intl.formatMessage({ id: 'BlockBuilder.listYourSpace' })}
                    </a>
                    <button
                      className={audienceCardLinkClass}
                      type="button"
                      onClick={() => openModal('services')}
                    >
                      {intl.formatMessage({ id: 'BlockBuilder.findHelp' })}
                    </button>
                  </div>
                </div>
              </Fragment>
            );
          }

          return (
            <Fragment key={`${blockId}_i${index}`}>
              <Block {...sharedBlockProps} />
            </Fragment>
          );
        } else {
          // If the block type is unknown, the app can't know what to render
          console.warn(`Unknown block type (${block.blockType}) detected inside (${sectionId}).`);
          return null;
        }
      })}
    </>
  );
};

export default BlockBuilder;
